// src/utils/colors.js
// Generatore deterministico di colori a partire da una stringa (es. "Mario Rossi").
// Usa un PRNG seedato dall'hash del nome e restituisce un HEX pieno e leggibile.

// Hash veloce deterministico (xfnv1a)
function hashString(str) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

// PRNG semplice (mulberry32) da seed intero
function mulberry32(seed) {
    return function () {
        let t = (seed += 0x6D2B79F5) >>> 0;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

// HSL -> HEX
function hslToHex(h, s, l) {
    s /= 100;
    l /= 100;
    const k = n => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    const toHex = x => Math.round(x * 255).toString(16).padStart(2, '0');
    return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

/**
 * Restituisce un colore HEX deterministico e “randomico”
 * basato su nome+cognome (case/whitespace-insensitive).
 *
 * Strategia:
 * - Hue da 0..360 “random” seedato.
 * - Saturation/Lightness fissate per buona leggibilità su sfondo scuro.
 * - Leggera variazione di L per distribuire meglio i toni simili.
 */
export function colorFromName(fullNameOrKey) {
    const key = String(fullNameOrKey || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!key) return '#C0FF03'; // fallback

    const seed = hashString(key);
    const rand = mulberry32(seed);

    // Hue “random” su tutto il cerchio
    const hue = Math.floor(rand() * 360);

    // Saturazione alta per colori vividi, Lightness medio-alta per contrasto su asfalto grigio
    const sat = 72;

    // Lightness con piccola variazione deterministica per differenziare nomi simili
    const baseLight = 52;
    const jitter = Math.floor(rand() * 14) - 7; // [-7, +6]
    const light = Math.max(40, Math.min(62, baseLight + jitter));

    return hslToHex(hue, sat, light);
}

/* ==== Chroma key (green screen) ============================================
 * Negli overlay OBS lo sfondo è verde puro e il filtro Chroma Key rende
 * trasparente ogni pixel di tinta verde, anche dentro le grafiche: un pilota
 * con colore verde, un settore verde o un accento lime sparirebbero. Qui le
 * tinte a rischio vengono spostate fuori dalla zona verde mantenendo
 * saturazione e luminosità: giallo-verdi verso il giallo, verdi verso il ciano.
 */
export const CHROMA_KEY_GREEN = '#00ff00';

function hexToHsl(hex) {
    let h = String(hex || '').trim().replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    if (d === 0) return { h: 0, s: 0, l: l * 100 };
    const s = d / (1 - Math.abs(2 * l - 1));
    let hue;
    if (max === r) hue = ((g - b) / d) % 6;
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue = (hue * 60 + 360) % 360;
    return { h: hue, s: s * 100, l: l * 100 };
}

/** Colore sicuro per il green screen: le tinte verdi (65°–175°) escono dalla zona del chroma key. */
export function chromaSafeColor(color) {
    const hsl = hexToHsl(color);
    if (!hsl || hsl.s < 25) return color;          // grigi e colori spenti non danno problemi
    if (hsl.h < 65 || hsl.h > 175) return color;   // già fuori dalla zona verde
    const h = hsl.h < 115
        ? 42 + (hsl.h - 65) * 0.3                  // giallo-verde → giallo/oro
        : 188 + (hsl.h - 115) * 0.5;               // verde → ciano/azzurro
    return hslToHex(Math.round(h), Math.round(hsl.s), Math.round(hsl.l));
}
